import { useMemo } from "react";
import { useI18n } from "@/lib/i18n";

/**
 * Rally-style Roadbook view: turn-by-turn tulip cards with partial and
 * cumulative distances between each significant direction change.
 * Renders a scrollable grid where each card shows:
 *   - Sequence number
 *   - Tulip icon (incoming line from bottom, outgoing arrow in the turn dir)
 *   - Partial distance from the previous turn
 *   - Cumulative distance from the start
 *   - Signed turn angle
 *
 * Props:
 *   turns:     result of extractTurns(polyline)  — {index, lat, lng, delta, direction, cumKm}[]
 *   totalKm:   route.distance_km — for the header summary
 */
export default function RoadbookView({ turns = [], totalKm = 0, driving = false, currentTurnIdx = 0, nearTurn = null }) {
  const { t, lang } = useI18n();

  // Compute partials: delta km between each consecutive turn, plus the
  // initial "from start" and final "to finish" legs.
  const entries = useMemo(() => {
    const list = [];
    let prevKm = 0;
    turns.forEach((tp, i) => {
      list.push({
        idx: i + 1,
        delta: tp.delta,
        direction: tp.direction,
        cumKm: tp.cumKm,
        partialKm: Math.max(0, tp.cumKm - prevKm),
      });
      prevKm = tp.cumKm;
    });
    // Final leg (from last turn to end)
    if (totalKm > prevKm + 0.05) {
      list.push({
        idx: turns.length + 1,
        final: true,
        cumKm: totalKm,
        partialKm: Math.max(0, totalKm - prevKm),
      });
    }
    return list;
  }, [turns, totalKm]);

  // Driving mode: single giant auto-advancing card. The current turn is
  // picked from `nearTurn` (live distance from GPS) when available,
  // otherwise from `currentTurnIdx` which is updated by useTracking as
  // the rider passes each polyline vertex.
  if (driving) {
    const activeTurn =
      nearTurn ||
      (turns[currentTurnIdx]
        ? { ...turns[currentTurnIdx], distanceKm: null }
        : null);
    return <DrivingCard turn={activeTurn} totalTurns={turns.length} idx={currentTurnIdx} lang={lang} totalKm={totalKm} />;
  }

  return (
    <div className="relative bg-brema-coal border border-white/10 p-4" data-testid="roadbook-view">
      <span className="corner-tick tl" />
      <span className="corner-tick tr" />
      <span className="corner-tick bl" />
      <span className="corner-tick br" />

      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] text-brema-yellow uppercase tracking-[0.3em]">
            // Roadbook
          </div>
          <h2 className="font-display font-black uppercase text-xl tracking-tight">
            {lang === "en" ? "Rally turns" : "Note di gara"}
          </h2>
        </div>
        <div className="text-right">
          <div className="font-mono text-[9px] text-zinc-300 uppercase tracking-widest">
            {turns.length} {lang === "en" ? "turns" : "svolte"} · {totalKm.toFixed(1)} {t("kmShort")}
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="py-12 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          {lang === "en" ? "No significant turns detected" : "Nessuna svolta significativa"}
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[520px] overflow-y-auto pr-1"
          data-testid="roadbook-grid"
        >
          {entries.map((e) => (
            <TulipCard key={e.idx} entry={e} lang={lang} />
          ))}
        </div>
      )}

      <div className="mt-4 font-mono text-[9px] text-zinc-500 uppercase tracking-widest">
        {lang === "en"
          ? "// tulips read bottom-to-top · IN from below · OUT along the arrow"
          : "// tulip letti dal basso · IN dal basso · OUT lungo la freccia"}
      </div>
    </div>
  );
}

function DrivingCard({ turn, totalTurns, idx, lang, totalKm }) {
  // No turn yet? Show a calm "on the road" placeholder so the driver
  // still sees the branded card, not an empty screen.
  if (!turn) {
    return (
      <div
        data-testid="roadbook-driving-empty"
        className="relative bg-brema-obsidian border border-brema-yellow/30 h-full w-full flex flex-col items-center justify-center p-8"
      >
        <div className="font-mono text-[10px] text-brema-yellow uppercase tracking-[0.3em] mb-3">
          // Roadbook · {lang === "en" ? "Driving" : "In marcia"}
        </div>
        <div className="font-display font-black text-white uppercase text-4xl leading-none tracking-tight">
          {lang === "en" ? "Straight ahead" : "Dritto"}
        </div>
        <div className="mt-4 font-mono text-[11px] text-zinc-400 uppercase tracking-widest">
          {lang === "en" ? "No turn coming up" : "Nessuna svolta in vista"}
        </div>
      </div>
    );
  }

  const isSharp = Math.abs(turn.delta) > 90;
  const isFinal = !!turn.final;
  const bgColor = isFinal ? "#C1272D" : isSharp ? "#C1272D" : "#FFD400";
  const fgColor = isFinal || isSharp ? "#FFFFFF" : "#131822";
  const dirLabel = isFinal
    ? lang === "en"
      ? "FINISH"
      : "ARRIVO"
    : turn.direction === "u-turn"
    ? lang === "en"
      ? "U-TURN"
      : "INVERSIONE A U"
    : turn.direction === "left"
    ? lang === "en"
      ? "TURN LEFT"
      : "SVOLTA A SINISTRA"
    : lang === "en"
    ? "TURN RIGHT"
    : "SVOLTA A DESTRA";

  const rotationDeg =
    turn.direction === "u-turn" ? 180 : turn.direction === "left" ? -90 : 90;

  const distanceLabel =
    turn.distanceKm != null
      ? turn.distanceKm >= 1
        ? `${turn.distanceKm.toFixed(1)} KM`
        : `${Math.round(turn.distanceKm * 1000)} M`
      : "—";

  return (
    <div
      data-testid="roadbook-driving-card"
      className="relative h-full w-full flex flex-col"
      style={{ background: bgColor, color: fgColor }}
    >
      {/* Top header strip: turn counter + progress along route */}
      <div
        className="flex items-center justify-between px-5 py-3 border-b-2"
        style={{ borderColor: fgColor }}
      >
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em]">
          {lang === "en" ? "Turn" : "Svolta"} {String(idx + 1).padStart(2, "0")}
          {totalTurns ? ` / ${String(totalTurns).padStart(2, "0")}` : ""}
        </span>
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em]">
          {totalKm ? `Σ ${totalKm.toFixed(1)} km` : ""}
        </span>
      </div>

      {/* Huge direction arrow */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {isFinal ? (
          <svg viewBox="0 0 24 24" width="180" height="180">
            <line x1="6" y1="22" x2="6" y2="2" stroke={fgColor} strokeWidth="1.5" />
            <path d="M6 2 L20 5 L6 10 Z" fill={fgColor} />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            width="200"
            height="200"
            style={{ transform: `rotate(${rotationDeg}deg)` }}
          >
            <path
              d="M12 2 L21 12 L15 12 L15 22 L9 22 L9 12 L3 12 Z"
              fill={fgColor}
            />
          </svg>
        )}

        {/* Distance countdown — the giant number the rider glances at */}
        <div
          data-testid="roadbook-driving-distance"
          className="mt-4 font-display font-black leading-none tracking-tight tabular-nums"
          style={{ fontSize: "clamp(3.5rem, 10vw, 6rem)" }}
        >
          {distanceLabel}
        </div>

        {/* Direction label */}
        <div
          data-testid="roadbook-driving-label"
          className="mt-2 font-display font-black uppercase tracking-widest text-center"
          style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}
        >
          {dirLabel}
        </div>
      </div>

      {/* Bottom hint bar */}
      <div
        className="px-5 py-2 border-t-2 font-mono text-[10px] uppercase tracking-widest text-center opacity-80"
        style={{ borderColor: fgColor }}
      >
        {lang === "en"
          ? "// hands on the bars · card advances automatically"
          : "// mani sul manubrio · la scheda avanza da sola"}
      </div>
    </div>
  );
}

function TulipCard({ entry, lang }) {
  const isFinal = !!entry.final;
  const partialM = Math.round(entry.partialKm * 1000);
  const partialLabel =
    entry.partialKm >= 1
      ? `${entry.partialKm.toFixed(1)} km`
      : `${partialM} m`;

  const isSharp = !isFinal && Math.abs(entry.delta) > 90;
  const bg = isFinal
    ? "bg-brema-red/15 border-brema-red/60"
    : isSharp
    ? "bg-brema-red/8 border-brema-red/40"
    : "bg-brema-obsidian/60 border-white/10";

  const dirLabel = isFinal
    ? lang === "en"
      ? "FINISH"
      : "ARRIVO"
    : entry.direction === "u-turn"
    ? lang === "en"
      ? "U-TURN"
      : "INV. U"
    : entry.direction === "left"
    ? lang === "en"
      ? "LEFT"
      : "SX"
    : lang === "en"
    ? "RIGHT"
    : "DX";

  return (
    <div
      data-testid={`roadbook-card-${entry.idx}`}
      className={`relative border ${bg} p-3 flex flex-col`}
    >
      {/* Header row: sequence number + direction pill */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-brema-yellow">
          #{String(entry.idx).padStart(2, "0")}
        </span>
        <span
          className={`font-display font-bold text-[10px] uppercase tracking-widest ${
            isFinal
              ? "text-brema-red"
              : isSharp
              ? "text-brema-red"
              : "text-brema-yellow"
          }`}
        >
          {dirLabel}
        </span>
      </div>

      {/* Tulip icon */}
      <div className="mt-2 flex items-center justify-center">
        <TulipSvg entry={entry} isFinal={isFinal} />
      </div>

      {/* Partial distance (from previous turn) */}
      <div className="mt-2 text-center">
        <div className="font-display font-bold text-2xl leading-none text-white">
          {partialLabel}
        </div>
        <div className="font-mono text-[9px] text-zinc-400 uppercase tracking-widest mt-1">
          {lang === "en" ? "since prev" : "da precedente"}
        </div>
      </div>

      {/* Cumulative + angle */}
      <div className="mt-2 border-t border-white/10 pt-2 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-zinc-400">
        <span>Σ {entry.cumKm.toFixed(1)} km</span>
        {!isFinal && (
          <span>
            {entry.delta > 0 ? "+" : ""}
            {Math.round(entry.delta)}°
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * SVG "tulip" schematic:
 *   - A vertical line coming IN from the bottom (S = start of the turn)
 *   - A short line/arrow going OUT in the direction of the turn
 * Angles: 0° = straight ahead (up), -180..180 signed.
 */
function TulipSvg({ entry, isFinal }) {
  const SIZE = 72;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const IN_END_Y = CY + 4; // in-arrow ends just past centre
  const R_OUT = 22; // out-arrow length

  if (isFinal) {
    // Finish flag icon
    return (
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <line x1={CX} y1={SIZE - 6} x2={CX} y2={12} stroke="#FCA311" strokeWidth="3" strokeLinecap="round" />
        <path
          d={`M ${CX} 12 L ${CX + 20} 18 L ${CX} 26 Z`}
          fill="#C1272D"
        />
        <circle cx={CX} cy={SIZE - 4} r="4" fill="#C1272D" />
      </svg>
    );
  }

  // Turn: draw incoming line from bottom to centre, then outgoing line
  // rotated by `delta` degrees (0° = straight up, +right, -left).
  const rad = ((entry.delta - 90) * Math.PI) / 180;
  // The outgoing line starts at centre, goes to (cx + R_OUT*cos(rad), cy + R_OUT*sin(rad))
  // rad above is measured with 0° = straight up (12 o'clock).
  // For an outbound arrow we point AWAY from centre, meaning: 0° stays up (-y direction).
  // So actual endpoint:
  const outX = CX + R_OUT * Math.cos(rad);
  const outY = CY + R_OUT * Math.sin(rad);

  const isSharp = Math.abs(entry.delta) > 90;
  const outColor = isSharp ? "#C1272D" : "#FCA311";

  // Arrow head at outgoing tip: two small barbs
  const headLen = 6;
  const headSpread = 6;
  const angleOut = Math.atan2(outY - CY, outX - CX);
  const barb1 = [
    outX - headLen * Math.cos(angleOut - Math.PI / headSpread),
    outY - headLen * Math.sin(angleOut - Math.PI / headSpread),
  ];
  const barb2 = [
    outX - headLen * Math.cos(angleOut + Math.PI / headSpread),
    outY - headLen * Math.sin(angleOut + Math.PI / headSpread),
  ];

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
      {/* Grid dot circle for aesthetics */}
      <circle
        cx={CX}
        cy={CY}
        r={SIZE / 2 - 4}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="1"
      />
      {/* IN line (from bottom = 6 o'clock) up to centre */}
      <line
        x1={CX}
        y1={SIZE - 6}
        x2={CX}
        y2={IN_END_Y}
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* Small "S" bullet at the entry */}
      <circle cx={CX} cy={SIZE - 6} r="3.5" fill="#FCA311" />

      {/* OUT arrow */}
      <line
        x1={CX}
        y1={CY}
        x2={outX}
        y2={outY}
        stroke={outColor}
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <polygon
        points={`${outX},${outY} ${barb1[0]},${barb1[1]} ${barb2[0]},${barb2[1]}`}
        fill={outColor}
      />
    </svg>
  );
}
